import React, { useState, useEffect,useRef } from "react";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import WorkerScheduleCompliance from "./WorkerScheduleCompliance";
import WorkerAttendanceReport from "./WorkerAttendanceReport";
import WorkerTaskReport from "./WorkerTaskReport";
import CustomButton from "../../../components/CustomButton";

const Reports = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [tempRange, setTempRange] = useState({ startDate: "", endDate: "" });
  const [isFiltered, setIsFiltered] = useState(false);

  // Get current month range
  const getCurrentMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    return { startDate: fmt(first), endDate: fmt(last) };
  };

  // Initialize date range on mount
  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      const range = getCurrentMonthRange();
      setDateRange(range);
    }
  }, []);

  const handleApply = () => {
    setOpenFilterModal(false);
    setIsFiltered(true);
    setDateRange(tempRange);
  };

  const handleClearFilter = () => {
    const range = getCurrentMonthRange();
    setDateRange(range);
    setIsFiltered(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

    const startRef = useRef(null);
  const endRef = useRef(null);  
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F9FAFB" }}>
      {/* Header with Filter */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "white",
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "22px",
            color: "#003149",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("sidebar.reports")}
        </Typography>

        {/* Date Range & Filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              border: "1px solid #C4C4C4",
              borderRadius: 1,
              px: 2,
              py: 1,
              bgcolor: "#F9FAFB",
              color: "#003149",
              fontSize: 13,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {formatDate(dateRange.startDate)} – {formatDate(dateRange.endDate)}
          </Box>

          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => {
                setTempRange(dateRange);
                setOpenFilterModal(true);
              }}
              sx={{
                border: "1px solid #C4C4C4",
                borderRadius: 1,
                bgcolor: isFiltered ? "#006EC2" : "transparent",
                color: isFiltered ? "#fff" : "#003149",
                "&:hover": {
                  bgcolor: isFiltered ? "#005bb0" : "#F3F4F6",
                },
              }}
            >
              <FilterAltIcon />
            </IconButton>
            {isFiltered && (
              <IconButton
                size="small"
                onClick={handleClearFilter}
                sx={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "#fff",
                  border: "1px solid #C4C4C4",
                  width: 18,
                  height: 18,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <CloseIcon sx={{ fontSize: 12, color: "#333" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* All Charts Dashboard - Scrollable */}
      <Box sx={{ pb: 4 }}>
        {/* Schedule Compliance Section */}
        <WorkerScheduleCompliance dateRange={dateRange} />
        
        {/* Attendance Charts Section */}
        <WorkerAttendanceReport dateRange={dateRange} />
        
        {/* Task Charts Section */}
        <WorkerTaskReport dateRange={dateRange} />
      </Box>

      {/* Filter Modal */}
      <Modal open={openFilterModal} onClose={() => setOpenFilterModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            width: 400,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#003149",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.filterByDate")}
            </Typography>
            <IconButton onClick={() => setOpenFilterModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#44546F",
                mb: 0.5,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.startDate")}
            </Typography>
            <input
               ref={startRef}
    onClick={() => startRef.current?.showPicker()}
              type="date"
              value={tempRange.startDate}
              onChange={(e) =>
                setTempRange((p) => ({ ...p, startDate: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #C4C4C4",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#44546F",
                mb: 0.5,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              {t("reportsScreen.endDate")}
            </Typography>
            <input
               ref={endRef}
    onClick={() => endRef.current?.showPicker()}
              type="date"
              value={tempRange.endDate}
              onChange={(e) =>
                setTempRange((p) => ({ ...p, endDate: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #C4C4C4",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CustomButton
              label={t("reportsScreen.apply")}
              size="medium"
              onClick={handleApply}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Reports;
